import { useState, useMemo } from 'react';
import { ArrowUpDown, Search, X, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AdminTabShell } from './AdminTabShell';
import { useToast } from '@/hooks/use-toast';
import {
  type BatteryAnomalyRow,
  type AnomalyType,
  type AnomalySeverity,
  type AnomalyStatus,
  type AllowedStatusUpdate,
  useAdminBatteryAnomalies,
  useUpdateAnomalyStatus,
} from '@/hooks/admin/useAdminBatteryAnomalies';

// ─── Labels ──────────────────────────────────────────────────────────────

const ANOMALY_TYPE_LABELS: Record<AnomalyType, string> = {
  sudden_capacity_drop: 'Capacity Drop',
  possible_module_loss: 'Module Loss Suspected',
  config_mismatch_suspected: 'Config / Nameplate Mismatch',
  insufficient_cycling: 'Insufficient Cycling',
  telemetry_quality_blocked: 'Telemetry Blocked',
};

const SEVERITY_LABELS: Record<AnomalySeverity, string> = {
  info: 'Info',
  watch: 'Watch',
  warning: 'Warning',
  critical: 'Critical',
};

const SEVERITY_CLASSES: Record<AnomalySeverity, string> = {
  info: 'bg-zinc-700/50 text-zinc-300',
  watch: 'bg-blue-900/50 text-blue-300',
  warning: 'bg-amber-900/50 text-amber-300',
  critical: 'bg-red-900/50 text-red-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  dormant: 'Dormant',
  reviewed: 'Reviewed',
  dismissed: 'Dismissed',
  promoted: 'Promoted',
};

const STATUS_CLASSES: Record<AnomalyStatus, string> = {
  dormant: 'bg-zinc-700/50 text-zinc-300',
  reviewed: 'bg-blue-900/50 text-blue-300',
  dismissed: 'bg-zinc-800/50 text-zinc-500',
  promoted: 'bg-amber-900/50 text-amber-300',
};

const BLOCKED_LABELS: Record<string, string> = {
  baseline_not_pinned: 'Baseline still learning',
  telemetry_missing: 'No telemetry data',
};

function blockedLabel(reason: string | null): string {
  if (!reason) return 'Actionable';
  return BLOCKED_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

const REASON_CODE_LABELS: Record<string, string> = {
  learned_exceeds_nameplate: 'Learned capacity exceeds nameplate',
  no_telemetry: 'No telemetry available',
  obs_count_below_threshold: 'Too few cycling observations',
  capacity_below_baseline: 'Capacity below established baseline',
  severe_drop: 'Severe capacity drop detected',
  sparse_telemetry: 'Sparse telemetry data',
  module_alignment_detected: 'Drop aligns with module loss pattern',
};

function reasonLabel(code: string): string {
  return REASON_CODE_LABELS[code] ?? code.replace(/_/g, ' ');
}

// ─── Recommended next steps ──────────────────────────────────────────────

function recommendedStep(row: BatteryAnomalyRow): string {
  switch (row.anomaly_type) {
    case 'config_mismatch_suspected':
      return 'Review nameplate capacity configuration for this property. The learned capacity estimates are inconsistent with the configured nameplate value.';
    case 'insufficient_cycling':
      return 'Wait for more charge/discharge cycles. The battery has not cycled enough for reliable capacity estimation.';
    case 'telemetry_quality_blocked':
      return 'Inspect telemetry quality. No usable data is being received from this system.';
    case 'sudden_capacity_drop':
      return 'Monitor closely. Recent observations show significant capacity loss compared to the established baseline. Check battery configuration and physical installation.';
    case 'possible_module_loss':
      return 'The capacity drop pattern aligns with losing one battery module from the stack. Verify all battery modules are online and communicating.';
    default:
      return 'Monitor next cron evaluation cycle.';
  }
}

function whyNoIssue(row: BatteryAnomalyRow): string {
  if (row.blocked_reason) {
    if (row.blocked_reason === 'baseline_not_pinned') {
      return 'The baseline is still learning. Anomaly detection requires a stable, pinned baseline before creating issues.';
    }
    if (row.blocked_reason === 'telemetry_missing') {
      return 'No telemetry data is available. Cannot evaluate capacity without data.';
    }
    if (row.blocked_reason.startsWith('only_')) {
      return `Not enough cycling observations yet (${row.blocked_reason.replace(/_/g, ' ')}). Need at least 3 non-outlier observations.`;
    }
    return `Blocked: ${row.blocked_reason.replace(/_/g, ' ')}`;
  }
  return 'PR-D dormant mode: all anomalies are detection-only. No issues or alerts are created.';
}

// ─── Filters / Sort ──────────────────────────────────────────────────────

type SortField = 'severity' | 'detected_at' | 'delta_pct' | 'confidence' | 'property_name';
type SortDir = 'asc' | 'desc';
type TypeFilter = 'all' | AnomalyType;
type SeverityFilter = 'all' | AnomalySeverity;
type StatusFilter = 'all' | 'active' | AnomalyStatus;

const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, watch: 2, info: 3 };
const CONFIDENCE_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2, insufficient_data: 3 };

function FilterButton<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Detail drawer ───────────────────────────────────────────────────────

function AnomalyDetailPanel({
  row,
  onClose,
  onStatusChange,
  isUpdating,
}: {
  row: BatteryAnomalyRow;
  onClose: () => void;
  onStatusChange: (status: AllowedStatusUpdate, note: string) => void;
  isUpdating: boolean;
}) {
  const [note, setNote] = useState('');
  const canAct = row.status !== 'dismissed' && row.status !== 'promoted';

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.property_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5">
              {ANOMALY_TYPE_LABELS[row.anomaly_type]}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${SEVERITY_CLASSES[row.severity]}`}>
            {SEVERITY_LABELS[row.severity]}
          </span>
          <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_CLASSES[row.status]}`}>
            {STATUS_LABELS[row.status]}
          </span>
          {row.blocked_reason && (
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-zinc-800/50 text-zinc-400">
              Blocked
            </span>
          )}
        </div>

        {/* Actions */}
        {canAct && (
          <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-3 text-xs">
            <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Actions</h3>
            <Textarea
              placeholder="Internal note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="h-16 text-xs bg-wt-bg-primary border-wt-border resize-none"
            />
            <div className="flex gap-2">
              {row.status === 'dormant' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  disabled={isUpdating}
                  onClick={() => onStatusChange('reviewed', note)}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Mark Reviewed
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs text-wt-text-muted"
                disabled={isUpdating}
                onClick={() => onStatusChange('dismissed', note)}
              >
                <XCircle className="h-3.5 w-3.5" />
                Dismiss
              </Button>
            </div>
            <p className="text-[10px] text-wt-text-muted leading-relaxed">
              {row.status === 'dormant'
                ? 'Reviewed means an operator has inspected this dormant candidate. Dismissed means this candidate should remain hidden from active review unless it reappears in a future evaluation.'
                : 'Dismiss this reviewed candidate to hide it from active review. If the anomaly reappears in a future evaluation, a new dormant row will be created.'}
            </p>
          </div>
        )}

        {row.status === 'dismissed' && (
          <div className="bg-wt-bg-secondary rounded-lg p-3 text-xs">
            <p className="text-wt-text-muted italic">
              This anomaly has been dismissed. If the condition reappears, a new dormant row will be created.
            </p>
          </div>
        )}

        {/* Capacity summary */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Capacity</h3>
          <div className="grid grid-cols-2 gap-2">
            {row.nameplate_kwh != null && (
              <div>
                <span className="text-wt-text-muted">Nameplate</span>
                <span className="ml-2 font-mono text-wt-text-primary">{Number(row.nameplate_kwh).toFixed(1)} kWh</span>
              </div>
            )}
            {row.expected_capacity_kwh != null && (
              <div>
                <span className="text-wt-text-muted">Expected</span>
                <span className="ml-2 font-mono text-wt-text-primary">{Number(row.expected_capacity_kwh).toFixed(1)} kWh</span>
              </div>
            )}
            {row.observed_capacity_kwh != null && (
              <div>
                <span className="text-wt-text-muted">Observed</span>
                <span className="ml-2 font-mono text-wt-text-primary">{Number(row.observed_capacity_kwh).toFixed(1)} kWh</span>
              </div>
            )}
            {row.delta_pct != null && (
              <div>
                <span className="text-wt-text-muted">Delta</span>
                <span className="ml-2 font-mono text-wt-text-primary">{Number(row.delta_pct).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Baseline info */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Baseline</h3>
          <div className="flex gap-3">
            <span className="text-wt-text-muted">Status:</span>
            <span className="text-wt-text-primary capitalize">{row.baseline_status ?? '—'}</span>
          </div>
          <div className="flex gap-3">
            <span className="text-wt-text-muted">Confidence:</span>
            <span className="text-wt-text-primary capitalize">{row.baseline_confidence ?? '—'}</span>
          </div>
        </div>

        {/* Reason codes */}
        {row.reason_codes.length > 0 && (
          <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
            <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Reason Codes</h3>
            <ul className="space-y-1">
              {row.reason_codes.map((c) => (
                <li key={c} className="text-wt-text-primary">{reasonLabel(c)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Blocked reason */}
        {row.blocked_reason && (
          <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
            <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Why Blocked</h3>
            <p className="text-wt-text-primary">{blockedLabel(row.blocked_reason)}</p>
          </div>
        )}

        {/* Why no issue */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Why No Issue Created</h3>
          <p className="text-wt-text-primary">{whyNoIssue(row)}</p>
        </div>

        {/* Recommended next step */}
        <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-2 text-xs">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Recommended Next Step</h3>
          <p className="text-wt-text-primary">{recommendedStep(row)}</p>
        </div>

        {/* Timestamps */}
        <div className="text-[11px] text-wt-text-muted space-y-1">
          <p>First detected: {new Date(row.created_at).toLocaleString()}</p>
          <p>Last evaluated: {new Date(row.detected_at).toLocaleString()}</p>
        </div>

        <p className="text-[10px] text-wt-text-muted italic">
          Dormant anomaly candidate. Not visible to customers. No issue created.
        </p>
      </div>
    </div>
  );
}

// ─── Main tab ────────────────────────────────────────────────────────────

export function BatteryAnomaliesTab() {
  const { data: rows, isLoading, error } = useAdminBatteryAnomalies(true);
  const updateStatus = useUpdateAnomalyStatus();
  const { toast } = useToast();

  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<BatteryAnomalyRow | null>(null);

  const handleStatusChange = (status: AllowedStatusUpdate, note: string) => {
    if (!selectedRow) return;
    updateStatus.mutate(
      { anomalyId: selectedRow.id, status, note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast({ title: `Anomaly marked as ${status}`, description: selectedRow.property_name });
          setSelectedRow(null);
        },
        onError: (err) => {
          toast({ title: 'Update failed', description: (err as Error).message, variant: 'destructive' });
        },
      },
    );
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    let list = rows;
    if (typeFilter !== 'all') list = list.filter((r) => r.anomaly_type === typeFilter);
    if (severityFilter !== 'all') list = list.filter((r) => r.severity === severityFilter);
    if (statusFilter === 'active') list = list.filter((r) => r.status === 'dormant' || r.status === 'reviewed');
    else if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.property_name.toLowerCase().includes(q));
    }
    return list;
  }, [rows, typeFilter, severityFilter, statusFilter, search]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'severity':
          return ((SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)) * dir;
        case 'detected_at':
          return (new Date(a.detected_at).getTime() - new Date(b.detected_at).getTime()) * dir;
        case 'delta_pct':
          return ((Number(a.delta_pct) || 0) - (Number(b.delta_pct) || 0)) * dir;
        case 'confidence':
          return ((CONFIDENCE_ORDER[a.confidence] ?? 9) - (CONFIDENCE_ORDER[b.confidence] ?? 9)) * dir;
        case 'property_name':
          return a.property_name.localeCompare(b.property_name) * dir;
        default:
          return 0;
      }
    });
  }, [filtered, sortField, sortDir]);

  // Summary counts
  const typeCounts = useMemo(() => {
    if (!rows) return {};
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.anomaly_type] = (counts[r.anomaly_type] ?? 0) + 1;
    return counts;
  }, [rows]);

  return (
    <AdminTabShell isLoading={isLoading} error={error as Error | null}>
      {/* Summary */}
      <div className="mb-4 flex flex-wrap gap-3">
        {Object.entries(ANOMALY_TYPE_LABELS).map(([type, label]) => {
          const count = typeCounts[type] ?? 0;
          if (count === 0) return null;
          return (
            <div key={type} className="bg-wt-bg-secondary border border-wt-border rounded px-3 py-1.5 text-xs">
              <span className="text-wt-text-muted">{label}:</span>{' '}
              <span className="font-mono font-medium text-wt-text-primary">{count}</span>
            </div>
          );
        })}
        <div className="bg-wt-bg-secondary border border-wt-border rounded px-3 py-1.5 text-xs">
          <span className="text-wt-text-muted">Total:</span>{' '}
          <span className="font-mono font-medium text-wt-text-primary">{rows?.length ?? 0}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 space-y-2">
        <div className="flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-wt-text-muted" />
          <Input
            placeholder="Search property..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs max-w-[220px] bg-wt-bg-secondary border-wt-border"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] text-wt-text-muted uppercase tracking-wider self-center mr-1">Type</span>
          <FilterButton label="All" value="all" current={typeFilter} onSelect={setTypeFilter} />
          {(Object.entries(ANOMALY_TYPE_LABELS) as [AnomalyType, string][]).map(([v, l]) => (
            <FilterButton key={v} label={l} value={v} current={typeFilter} onSelect={setTypeFilter} />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] text-wt-text-muted uppercase tracking-wider self-center mr-1">Severity</span>
          <FilterButton label="All" value="all" current={severityFilter} onSelect={setSeverityFilter} />
          {(Object.keys(SEVERITY_LABELS) as AnomalySeverity[]).map((v) => (
            <FilterButton key={v} label={SEVERITY_LABELS[v]} value={v} current={severityFilter} onSelect={setSeverityFilter} />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] text-wt-text-muted uppercase tracking-wider self-center mr-1">Status</span>
          <FilterButton label="All" value="all" current={statusFilter} onSelect={setStatusFilter} />
          <FilterButton label="Active" value="active" current={statusFilter} onSelect={setStatusFilter} />
          {(Object.keys(STATUS_LABELS) as AnomalyStatus[]).map((v) => (
            <FilterButton key={v} label={STATUS_LABELS[v]} value={v} current={statusFilter} onSelect={setStatusFilter} />
          ))}
        </div>
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <div className="text-center py-12 text-sm text-wt-text-muted">
          {rows?.length === 0
            ? 'No dormant anomalies detected. All batteries are healthy or still learning.'
            : 'No anomalies match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-wt-text-muted border-b border-wt-border">
                <SortHeader label="Property" field="property_name" current={sortField} dir={sortDir} onToggle={toggleSort} />
                <th className="text-left py-1.5 px-2 font-medium">Type</th>
                <SortHeader label="Severity" field="severity" current={sortField} dir={sortDir} onToggle={toggleSort} />
                <SortHeader label="Confidence" field="confidence" current={sortField} dir={sortDir} onToggle={toggleSort} />
                <th className="text-left py-1.5 px-2 font-medium">Status</th>
                <th className="text-right py-1.5 px-2 font-medium">Expected kWh</th>
                <th className="text-right py-1.5 px-2 font-medium">Observed kWh</th>
                <SortHeader label="Delta %" field="delta_pct" current={sortField} dir={sortDir} onToggle={toggleSort} align="right" />
                <th className="text-left py-1.5 px-2 font-medium">Blocked</th>
                <SortHeader label="Last Detected" field="detected_at" current={sortField} dir={sortDir} onToggle={toggleSort} />
                <th className="py-1.5 px-1 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedRow(row)}
                  className="border-b border-wt-border/50 hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                >
                  <td className="py-1.5 px-2 max-w-[160px] truncate">{row.property_name}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{ANOMALY_TYPE_LABELS[row.anomaly_type]}</td>
                  <td className="py-1.5 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SEVERITY_CLASSES[row.severity]}`}>
                      {SEVERITY_LABELS[row.severity]}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 capitalize">{row.confidence}</td>
                  <td className="py-1.5 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_CLASSES[row.status]}`}>
                      {STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {row.expected_capacity_kwh != null ? Number(row.expected_capacity_kwh).toFixed(1) : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {row.observed_capacity_kwh != null ? Number(row.observed_capacity_kwh).toFixed(1) : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {row.delta_pct != null ? `${Number(row.delta_pct).toFixed(1)}%` : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-wt-text-muted whitespace-nowrap">
                    {blockedLabel(row.blocked_reason)}
                  </td>
                  <td className="py-1.5 px-2 text-wt-text-muted whitespace-nowrap">
                    {new Date(row.detected_at).toLocaleDateString()}
                  </td>
                  <td className="py-1.5 px-1">
                    <ChevronRight className="h-3.5 w-3.5 text-wt-text-muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[10px] text-wt-text-muted">
        Dormant anomaly candidates. Internal only — not visible to customers. No issues or alerts created.
      </p>

      {/* Detail drawer */}
      {selectedRow && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setSelectedRow(null)}
          />
          <AnomalyDetailPanel
            row={selectedRow}
            onClose={() => setSelectedRow(null)}
            onStatusChange={handleStatusChange}
            isUpdating={updateStatus.isPending}
          />
        </>
      )}
    </AdminTabShell>
  );
}

// ─── Sort header helper ──────────────────────────────────────────────────

function SortHeader({
  label, field, current, dir, onToggle, align = 'left',
}: {
  label: string;
  field: SortField;
  current: SortField;
  dir: SortDir;
  onToggle: (f: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = field === current;
  return (
    <th
      className={`py-1.5 px-2 font-medium cursor-pointer select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onToggle(field)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active ? 'text-wt-accent' : 'text-wt-text-muted/50'}`} />
        {active && <span className="text-[9px] text-wt-accent">{dir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );
}
